// Copyright (c) Docugami, Inc. All rights reserved.
import 'react-app-polyfill/ie9';
import React from 'react'
import { Provider } from "react-redux";
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './components/shell/presentation/App';
import * as serviceWorker from './serviceWorker';

import { ClientConfigurationApi } from './networking/api'
import { Route, Routes } from 'react-router-dom';

import "./components/shell/styles/shell.scss";
import { getDebugLoadDelay } from './utils/localStorageHelpers';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { AppInsightsPlugin, AppInsightsTelemetryProvider } from 'telemetry/AppInsightsTelemetryProvider';
import { Telemetry } from 'telemetry/TelemetryManager';
import { RequestScheduler } from 'networking/RequestScheduler';
import { Config } from 'config';
import { Logger, LoggerCategory } from 'utils/Logger';
import { ITelemetryProvider } from 'telemetry/TelemetryProvider';
import { Auth0Provider } from '@auth0/auth0-react';
import axios from 'axios';
import { Suspense, lazy } from 'react';
import { authAccessor } from 'authentication/authAccessor';
import { PageLoadingFallback } from 'components/common/PageLoadingFallback';
import { CallbackPage } from 'components/shell/presentation/CallbackPage';
import { Logout, OrgSwitch } from 'components/shell/presentation/Login';
import { NotFoundPage } from 'pages/NotFoundPage';
import { ScheduledMaintenancePage } from 'pages/MaintenancePage';
import { store } from './store';
import { frontendSlice } from 'store/frontend/frontendSlice';
import { GoogleAnalyticsTelemetryProvider } from 'telemetry/GoogleAnalyticsTelemetryProvider';
import { ThemeProvider } from 'theming';
import { sleep } from 'utils/AsyncHelpers';

// Redirect component for storage auth callback (route no longer used but facilitates transition)
const StorageAuthRedirect = () => {
    window.location.replace(`${Config.storageUrl}/connections/oauth/callback${window.location.search}`);
    return null;
};

// Lazy-loaded page components for code splitting
const SessionExpiredPage = lazy(() => import('pages/SessionExpiredPage').then(m => ({ default: m.SessionExpiredPage })));
const EmailVerificationPage = lazy(() => import('pages/EmailVerificationPage').then(m => ({ default: m.EmailVerificationPage })));
require('polyfill-object.fromentries');

Logger.config = Config.logger;

export const requestScheduler = new RequestScheduler(axios);

const { detect } = require('detect-browser');
export const browser = detect();

if (browser.name === "ie") {
    const ieRoot = createRoot(document.getElementById('root')!);
    ieRoot.render(<div>
        <div style={{ width: '100%', textAlign: 'center', paddingTop: '10px' }}>We're sorry, but Internet Explorer is not supported by Docugami at this time.</div>
        <div style={{ width: '100%', textAlign: 'center', paddingTop: '10px' }}>Please consider using Google Chrome or the new Microsoft Edge for best results.</div>
    </div>);
}
else {
    authAccessor.setOrganizationId(() => {
        return store.getState().organizations.currentOrganization?.id ?? ''
    });

    getAppInsightsConnectionString().then(
        async (appInsightsConnectionString: string) => {
            const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href');
            const rootElement = document.getElementById('root')!;
            const root = createRoot(rootElement);

            function render() {

                if (!!appInsightsConnectionString) {
                    const appInsightsTelemetryProvider: ITelemetryProvider = new AppInsightsTelemetryProvider(appInsightsConnectionString);
                    Telemetry.register(appInsightsTelemetryProvider);
                }

                if (!!Config.googleAnalyticsKey) {
                    const googleAnalyticsTelemetryProvider: ITelemetryProvider = new GoogleAnalyticsTelemetryProvider(Config.googleAnalyticsKey);
                    Telemetry.register(googleAnalyticsTelemetryProvider);
                }

                // show the cookie warning 1 second after rendering
                setTimeout(() => setCookieTrackingUXVisibility(), 1000)

                root.render(
                    <Provider store={store}>
                        <ThemeProvider>
                            <Auth0Provider
                                domain={Config.auth0.domain}
                                clientId={Config.auth0.clientId}
                                redirectUri={`${Config.baseUrl}/callback`}
                                audience={Config.auth0.audience}
                                scope={Config.auth0.scope}
                                useRefreshTokens={true}
                                cacheLocation="localstorage">
                                <AppInsightsContext.Provider value={AppInsightsPlugin.instance}>
                                    <BrowserRouter basename={baseUrl ?? undefined}>
                                        <Suspense fallback={<PageLoadingFallback />}>
                                        <Routes>
                                            <Route path="/verify-email" element={<EmailVerificationPage />} />
                                            <Route path='/session-expired' element={<SessionExpiredPage />} />
                                            <Route path='/callback' element={<CallbackPage />} />
                                            <Route path='/storage/auth/callback' element={<StorageAuthRedirect />} />
                                            <Route path='/logout' element={<Logout />} />
                                            <Route path='/org/switch' element={<OrgSwitch />} />
                                            <Route path='/page-not-found' element={<NotFoundPage />} />
                                            <Route path="/*" element={<App />} />
                                        </Routes>
                                        </Suspense>
                                    </BrowserRouter>
                                </AppInsightsContext.Provider>
                            </Auth0Provider>
                        </ThemeProvider>
                    </Provider>
                );
            }

            function setupAppWithDebugDelay() {
                const delay = getDebugLoadDelay();
                if (delay === 0) {
                    render()
                } else {
                    setTimeout(() => render(), delay);
                }
            }

            const renderMaintenancePage = () => root.render(<Provider store={store}><ThemeProvider><ScheduledMaintenancePage /></ThemeProvider></Provider>);

            async function checkIfHealthy() {
                const queryResponse = await store.dispatch(frontendSlice.endpoints.health.initiate());
                return queryResponse?.data === true;
            }

            // Check frontend service health before rendering the app
            // If we fail, ensure the frontend service is not healthy by attempting to ping again after 3s,
            // then render the scheduled maintenance page
            let isHealthy = await checkIfHealthy();
            if (!isHealthy) {
                await sleep(3000);
                isHealthy = await checkIfHealthy();
            }

            if (!isHealthy) {
                renderMaintenancePage()
            } else {
                setupAppWithDebugDelay()
            }
        });
}

async function getAppInsightsConnectionString() {
    let appInsightsConnectionString = Config.appInsights.connectionString;
    if (!appInsightsConnectionString) {
        const clientConfigurationApi = new ClientConfigurationApi();
        try {
            const response = await clientConfigurationApi.clientConfigurationConnectionString();
            appInsightsConnectionString = response.data;
        }
        catch (error) {
            Logger.LogError(LoggerCategory.Telemetry, "Failed to retrieve app insights key.");
        }
    }

    return appInsightsConnectionString;
}


function setCookieTrackingUXVisibility(): void {
    // show the cookie warning (it is hidden by default)
    let suppressCookieWarningCss = "div.cookie-confirmation-display { display: inline; }";
    var styleElem = document.createElement('style');
    styleElem.appendChild(document.createTextNode(suppressCookieWarningCss));
    document.getElementsByTagName('head')[0].appendChild(styleElem);
}


// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
